import { morph, MORPH_OPTIONS } from '@theme/morph';
import { prepareComponents } from '@theme/component-loader';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending renders by section ID
   * @type {Map<string, { abortController: AbortController, promise: Promise<string> }>}
   */
  #pendingRendersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {boolean} [options.injectStylesheet=false] - When true, extracts
   * `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full', injectStylesheet = false } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const renderPromise = this.#renderSection(sectionId, { cache, mode, injectStylesheet, url }, abortController);
    this.#pendingRendersBySectionId.set(sectionId, { abortController, promise: renderPromise });

    return renderPromise;
  }

  /**
   * Renders a section with an abort controller.
   * @param {string} sectionId - The section ID
   * @param {Object} options - The options
   * @param {boolean} options.cache - Whether to use the cache
   * @param {'hydration'|'full'} options.mode - Which parts of the section to morph into the DOM
   * @param {boolean} options.injectStylesheet - When true, injects stylesheet from the response
   * @param {URL} [options.url] - The URL to render the section from
   * @param {AbortController} abortController - The abort controller for this render
   * @returns {Promise<string>} The rendered section HTML
   */
  async #renderSection(sectionId, { cache, mode, injectStylesheet, url }, abortController) {
    let sectionHTML = '';

    try {
      sectionHTML = await this.getSectionHTML(sectionId, cache, url, abortController.signal);
      await morphSection(sectionId, sectionHTML, { mode, injectStylesheet, signal: abortController.signal });
    } catch (error) {
      if (abortController.signal.aborted) {
        const pendingRender = this.#pendingRendersBySectionId.get(sectionId);
        if (pendingRender && pendingRender.abortController !== abortController) {
          return pendingRender.promise;
        }

        return sectionHTML;
      }

      throw error;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }

      const pendingRender = this.#pendingRendersBySectionId.get(sectionId);
      if (pendingRender?.abortController === abortController) {
        this.#pendingRendersBySectionId.delete(sectionId);
      }
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing render for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
      this.#abortControllersBySectionId.delete(sectionId);
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {AbortSignal} [signal] - A signal that cancels the section render fetch
   * @param {boolean} [validate=true] - Whether to validate the section wrapper before returning
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), signal, validate = true) {
    signal?.throwIfAborted();
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    const shouldSharePendingPromise = !signal;
    if (shouldSharePendingPromise) {
      const pendingPromise = this.#pendingPromises.get(sectionUrl);
      if (pendingPromise) return pendingPromise;
    }

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) {
        this.#cache.delete(sectionUrl);
        this.#cache.set(sectionUrl, cachedHTML);
        return cachedHTML;
      }
    }

    const request = async () => {
      const response = await fetch(sectionUrl, { signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const sectionHTML = await response.text();
      signal?.throwIfAborted();
      if (validate || useCache) {
        const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
        if (!parsed.getElementById(buildSectionSelector(sectionId))) {
          throw new Error(`Section ${sectionId} missing from response`);
        }
      }
      if (useCache) this.#cacheHTML(sectionUrl, sectionHTML);
      return sectionHTML;
    };
    const pendingPromise = request();
    if (!shouldSharePendingPromise) return pendingPromise;
    this.#pendingPromises.set(sectionUrl, pendingPromise);
    try {
      return await pendingPromise;
    } finally {
      if (this.#pendingPromises.get(sectionUrl) === pendingPromise) this.#pendingPromises.delete(sectionUrl);
    }
  }

  /** @param {string} url @param {string} html */
  #cacheHTML(url, html) {
    this.#cache.delete(url);
    this.#cache.set(url, html);
    while (this.#cache.size > 20) {
      const oldest = this.#cache.keys().next().value;
      if (oldest === undefined) break;
      this.#cache.delete(oldest);
    }
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.has(url) || containsShadowRoot(section)) continue;
      this.#cacheHTML(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url = new URL(url);
  url.hash = '';
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${normalizeSectionId(sectionId)}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {Object} [options] - Additional options
 * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 * @param {boolean} [options.injectStylesheet=false] - When true, extracts
 * `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
 * @param {AbortSignal} [options.signal] - Cancels a superseded render before DOM changes.
 */
export async function morphSection(sectionId, html, options = {}) {
  const { mode = 'full', injectStylesheet = false, signal } = options;
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  await prepareComponents(newElement);
  signal?.throwIfAborted();
  if (injectStylesheet) {
    normalizeSectionStylesheet(fragment, newElement, existingElement);
  }
  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });
}

/**
 * Places the response stylesheet in the new wrapper, regardless of where the
 * Section Rendering API put it, and updates the live style before new elements
 * are connected by the morph. Keep the current style if the response omits it.
 * @param {Document} fragment
 * @param {HTMLElement} newElement
 * @param {HTMLElement} existingElement
 */
function normalizeSectionStylesheet(fragment, newElement, existingElement) {
  const selector = 'style[data-section-stylesheet]';
  const incomingStyles = [...fragment.querySelectorAll(selector)];
  const incomingStyle = newElement.querySelector(selector) ?? incomingStyles[0];
  const liveStyles = [...existingElement.querySelectorAll(selector)];
  const liveStyle = liveStyles[0];
  const preparedStyle = incomingStyle ?? liveStyle?.cloneNode(true);

  for (const style of incomingStyles) style.remove();
  if (preparedStyle) newElement.prepend(preparedStyle);

  for (const style of liveStyles.slice(1)) style.remove();
  if (!preparedStyle) return;
  if (liveStyle) {
    liveStyle.textContent = preparedStyle.textContent;
    if (liveStyle.parentElement !== existingElement) existingElement.prepend(liveStyle);
  } else {
    existingElement.prepend(preparedStyle.cloneNode(true));
  }
}

export const sectionRenderer = new SectionRenderer();
